import { useState, type CSSProperties, type ReactNode } from "react";

// RelayStudio chrome (ADR 0087) — the RelayStudio.html reference's dense operator
// switcher: its spacing, layout, and dual-bus behavior are preserved verbatim (the
// ratified directive stands — the consoles are NOT re-written). What changed
// (per the "make the live consoles match the admin, adjust colors/buttons" directive
// extending ADR 0164 to operators): the design LANGUAGE is harmonized to the admin
// palette (adminTheme.css) so the live consoles read as the same product as the admin
// portal + operator cave console. Grounds/panels/hairlines adopt the admin ink scale,
// text adopts the warm admin creams, and the fonts become Archivo (sans) + IBM Plex
// Mono (mono) — the same families the admin uses (loaded globally via adminTheme.css).
// The FUNCTIONAL broadcast semantics are kept but pointed at the admin's SEMANTIC
// status colours: red = PROGRAM/on-air (admin --live), blue = PREVIEW/staged (admin
// --info), green = live-ok (admin --ok), and yellow stays the A-slot identity
// (amber, ADR 0108 — orthogonal to the preview/program tally; B stays violet inline).
export const SC = {
  bg: "#0b0b0d",       // admin --ink-950 (ground)
  panel: "#16161a",    // admin --ink-850
  panel2: "#1c1c21",   // admin --ink-800
  stageBg: "#08080a",  // just below the ground for the confidence stage
  meterBg: "#111114",  // admin --ink-900
  hair: "rgba(255,255,255,0.06)",   // admin --line-hairline
  hair2: "rgba(255,255,255,0.08)",  // admin --line
  hair3: "rgba(255,255,255,0.12)",  // admin --line-strong
  divider: "#2e2e36",  // admin --ink-600
  text: "#f2f0ea",     // admin --text-1 (warm cream)
  text2: "#b8b3a8",    // admin --text-2
  dim: "#8b867c",      // admin --text-3
  dim2: "#8b867c",     // admin --text-3
  dim3: "#b8b3a8",     // admin --text-2
  faint: "#b8b3a8",    // admin --text-2
  red: "#f0644a",      // admin --live (PROGRAM / on-air)
  redSoft: "#f5897a",
  redTint: "rgba(240,100,74,0.16)",
  redTint2: "rgba(240,100,74,0.14)",
  redEdge: "rgba(240,100,74,0.5)",
  blue: "#6ba9e8",     // admin --info (PREVIEW / staged bus)
  blueHi: "#93c1f0",
  blueTint: "rgba(107,169,232,0.16)",
  blueEdge: "rgba(107,169,232,0.5)",
  green: "#6bbf59",    // admin --ok
  yellow: "#ffd60a",   // A-slot identity (amber, ADR 0108) — unchanged
  neutralBtn: "#24242b",  // admin --ink-700 (neutral button surface)
  chipBg: "#1c1c21",      // admin --ink-800
  mono: "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  sans: "Archivo, -apple-system, 'SF Pro Text', system-ui, sans-serif",
} as const;

/** A dashboard section card — #1c1c1e / hairline / radius 12 / pad 11, with a mono
 *  label header and an optional right-aligned subtitle (grey). When `collapsible`,
 *  the mono label becomes a caret toggle (accessible `aria-expanded` + "Collapse ·/
 *  Expand ·" name) that shows/hides the body, so a verbose/secondary setup rack can
 *  be folded away to cut clutter (ADR 0113); `defaultCollapsed` opens it folded. The
 *  `right` summary stays visible when collapsed. Non-collapsible panels are
 *  unchanged. */
export function SCPanel({
  label,
  right,
  children,
  ariaLabel,
  style,
  collapsible = false,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  onToggleCollapsed,
}: {
  label: string;
  right?: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
  style?: CSSProperties;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  // Optional CONTROLLED collapse (ADR 0120): when `collapsed` is provided the caller
  // owns the state (so it can be lifted above the tab-switch unmount and persisted);
  // `onToggleCollapsed` is called on the caret. Omit both to keep the internal,
  // per-mount state (today's behavior) — fully backward-compatible.
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  const [collapsedState, setCollapsedState] = useState(defaultCollapsed);
  const controlled = collapsedProp !== undefined;
  const collapsed = controlled ? collapsedProp : collapsedState;
  const toggle = () => (controlled ? onToggleCollapsed?.() : setCollapsedState((c) => !c));
  const isCollapsed = collapsible && collapsed;
  const labelStyle: CSSProperties = { fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim };
  return (
    <section
      aria-label={ariaLabel ?? label}
      style={{
        background: SC.panel,
        border: `1px solid ${SC.hair}`,
        borderRadius: 12,
        padding: 11,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        {collapsible ? (
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
            onClick={toggle}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: 0, cursor: "pointer", ...labelStyle }}
          >
            <span aria-hidden style={{ fontSize: 8, display: "inline-block", transform: collapsed ? "rotate(-90deg)" : "none" }}>▼</span>
            {label}
          </button>
        ) : (
          <div style={labelStyle}>{label}</div>
        )}
        {right != null && <div style={{ fontSize: 11, color: SC.dim2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{right}</div>}
      </div>
      {!isCollapsed && children}
    </section>
  );
}

/** A tally-framed 16:9 monitor (PREVIEW = blue, PROGRAM = red) — the header band +
 *  the 16:9 stage. `children` is the real video composition; overlays (lower-third /
 *  bug / ticker) may be layered by the caller inside the stage. */
export function SCMonitor({
  bus,
  name,
  slug,
  children,
  overlay,
  guides = true,
}: {
  bus: "preview" | "program";
  name: ReactNode;
  slug?: ReactNode;
  children: ReactNode;
  overlay?: ReactNode;
  guides?: boolean;
}) {
  const isPgm = bus === "program";
  const edge = isPgm ? "rgba(255,69,58,0.45)" : SC.hair;
  const bandBg = isPgm ? SC.redTint : "rgba(10,132,255,0.13)";
  const bandEdge = isPgm ? "rgba(255,69,58,0.35)" : "rgba(10,132,255,0.3)";
  const fg = isPgm ? SC.redSoft : SC.blueHi;
  return (
    <div
      style={{
        background: SC.panel,
        border: `1px solid ${edge}`,
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        flex: "1 1 0",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          minWidth: 0,
          whiteSpace: "nowrap",
          padding: "4px 8px",
          background: bandBg,
          borderBottom: `1px solid ${bandEdge}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
          {isPgm && <span style={{ width: 7, height: 7, borderRadius: "50%", background: SC.red, animation: "tally 1.6s ease-in-out infinite", flex: "none" }} />}
          <span style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.07em", color: fg }}>{isPgm ? "PROGRAM" : "PREVIEW"}</span>
        </div>
        <span style={{ fontSize: 12, color: fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      </div>
      <div style={{ padding: 5, display: "flex" }}>
        <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", borderRadius: 12, overflow: "hidden", background: SC.stageBg }}>
          {children}
          {guides && <div style={{ position: "absolute", inset: 6, border: "1px dashed rgba(255,255,255,0.14)", borderRadius: 3, pointerEvents: "none" }} />}
          {overlay}
          {slug != null && (
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "4px 8px", background: "rgba(16,16,18,0.75)", fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: "#ebebf0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{slug}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** The mock's lower-third band, drawn inside a monitor stage. */
export function SCLowerThird({ line1, line2, accent = SC.yellow }: { line1: ReactNode; line2?: ReactNode; accent?: string }) {
  return (
    <div style={{ position: "absolute", left: 10, bottom: 26, display: "flex", flexDirection: "column", gap: 2, maxWidth: "66%" }}>
      <div style={{ background: accent, color: "#141416", padding: "5px 12px", fontSize: 13, fontWeight: 600, borderRadius: "3px 3px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{line1}</div>
      {line2 != null && line2 !== "" && (
        <div style={{ background: "rgba(28,28,30,0.94)", color: SC.text, padding: "4px 12px", fontSize: 10, borderRadius: "0 0 3px 3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{line2}</div>
      )}
    </div>
  );
}

/** A generic pill button in the mock language (used for tabs, transitions, durations). */
export function SCPill({
  label,
  active,
  activeColor = SC.blue,
  onClick,
  ariaLabel,
  ariaPressed,
  disabled,
  style,
  mono,
  flex,
}: {
  label: ReactNode;
  active?: boolean;
  activeColor?: string;
  onClick?: () => void;
  ariaLabel?: string;
  ariaPressed?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  mono?: boolean;
  flex?: number;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: flex != null ? `${flex}` : undefined,
        padding: "9px 15px",
        borderRadius: 999,
        border: `1px solid ${active ? activeColor : SC.hair3}`,
        background: active ? activeColor : SC.chipBg,
        color: active ? "#ffffff" : SC.dim,
        fontFamily: mono ? SC.mono : SC.sans,
        fontSize: 13,
        fontWeight: 450,
        cursor: disabled ? "default" : "pointer",
        whiteSpace: "nowrap",
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {label}
    </button>
  );
}
